/**
 * useAutosave hook - Thin debounced trigger for saving data.
 *
 * This hook manages the timer and "dirty" state for autosaving.
 * It does NOT handle network logic, retries, or conflict resolution.
 * Those responsibilities are delegated to the provided saveFunction.
 */

import {
  useEffect, useRef, useCallback,
} from 'react';
import log from '../../utils/logger';

export interface UseAutosaveOptions {
  /** The function that performs the actual save. Should return a promise. */
  saveFunction?: () => Promise<unknown>;
  /** Whether there are unsaved changes. */
  isDirty?: boolean;
  /** A value to watch for changes to trigger the debounce timer. */
  watchValue?: unknown;
  /** Whether autosaving is currently enabled. */
  enabled?: boolean;
  /** Debounce delay in milliseconds. */
  debounceMs?: number;
  /** Optional key for global serialization across component instances. */
  lockKey?: string;
  /** Callback for successful save. */
  onSaveSuccess?: () => void;
  /** Callback for save error. */
  onSaveError?: (error: unknown) => void;
}

export interface UseAutosaveReturn {
  /** Manually trigger a save now, bypassing the debounce timer. */
  saveNow: () => Promise<void>;
  /** Cancel any pending debounced save. */
  cancel: () => void;
}

export const useAutosave = (options: UseAutosaveOptions = {}): UseAutosaveReturn => {
  const {
    saveFunction,
    isDirty = false,
    watchValue,
    enabled = true,
    debounceMs = 2000,
    onSaveSuccess,
    onSaveError,
  } = options;

  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savePromiseRef = useRef<Promise<unknown> | null>(null);

  // Stable refs to avoid unnecessary re-runs of the timer effect
  const saveFnRef = useRef(saveFunction);
  const onSaveSuccessRef = useRef(onSaveSuccess);
  const onSaveErrorRef = useRef(onSaveError);
  const isDirtyRef = useRef(isDirty);
  const enabledRef = useRef(enabled);

  useEffect(() => { saveFnRef.current = saveFunction; }, [saveFunction]);
  useEffect(() => { onSaveSuccessRef.current = onSaveSuccess; }, [onSaveSuccess]);
  useEffect(() => { onSaveErrorRef.current = onSaveError; }, [onSaveError]);
  useEffect(() => { isDirtyRef.current = isDirty; }, [isDirty]);
  useEffect(() => { enabledRef.current = enabled; }, [enabled]);

  // Clear timeout on unmount
  useEffect(() => () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  const performSave = useCallback(async (): Promise<void> => {
    const canSave = !!saveFnRef.current && isDirtyRef.current && enabledRef.current;

    if (!canSave) {
      if (isDirtyRef.current && enabledRef.current && !saveFnRef.current) {
        log.warn('[useAutosave] saveFunction is missing despite unsaved changes.');
      }
      return;
    }

    // Serialization: Don't start a new save if one is already in flight.
    // The saveFunction itself should ideally handle AbortControllers if it wants to cancel.
    if (savePromiseRef.current) {
      log.info('[useAutosave] Save skipped - Another save is already in flight.');
      return;
    }

    try {
      savePromiseRef.current = saveFnRef.current!();
      await savePromiseRef.current;

      if (onSaveSuccessRef.current) {
        onSaveSuccessRef.current();
      }
    } catch (error) {
      log.error('[useAutosave] Save failed - Error in saveFunction execution', error);
      if (onSaveErrorRef.current) {
        onSaveErrorRef.current(error);
      }
    } finally {
      savePromiseRef.current = null;
    }
  }, []);

  // Trigger debounce timer when watchValue changes
  useEffect(() => {
    if (!isDirtyRef.current || !enabledRef.current) {
      // Cancel any pending save if autosave is disabled
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      return () => {};
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      performSave();
    }, debounceMs);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [watchValue, debounceMs, performSave]);

  const saveNow = useCallback(async (): Promise<void> => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    await performSave();
  }, [performSave]);

  const cancel = useCallback((): void => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return {
    saveNow,
    cancel,
  };
};
